/**
 * The SAM session: pick a point, see a mask, save it — or decide not to and
 * close without a single byte fetched.
 *
 * `ToolLaunchDialog`'s Continue button is what opens this (see its
 * `tool.interactive` branch) once a volume is chosen; everything from here on
 * — the model-download consent, the click loop, the save — is this panel's
 * own, and nothing in it runs before the reader presses "Download & run".
 */
import * as React from "react";
import { Download, Loader2, Save } from "lucide-react";

import { Button } from "@/components/ui/primitives";
import { DialogFooter, DialogOverlay, DialogPanel, DialogTitle, dialogPrimaryButtonClass, dialogSecondaryButtonClass } from "@/components/ui/dialog";
import { getAssetUrl } from "@/ipc/api";
import { niftiGz } from "@/lib/export";
import { cn } from "@/lib/utils";
import type { NiftiVolume } from "@/lib/nifti";
import { registerToolOutput } from "@/wasm-engine/engine";
import { useCurrentCase } from "@/store/appStore";
import { percentileClip, type SliceOrientation } from "@/viewer/SliceCanvas";
import { loadVolume } from "@/viewer/volumeCache";
import { nodeAbsPath } from "@/viewer/viewerStore";
import { PromptCanvas, type SamPoint } from "./PromptCanvas";
import { createSamSession, type SamSession } from "./samClient";
import {
  buildEncoderInput,
  downsampleMask,
  embedMaskIntoVolume,
  extractSlice,
  sliceDepth,
  toModelSpace,
} from "./samMath";
import { SAM_LICENSE_NOTE, SAM_TOTAL_MB } from "./samModel";
import { useInteractiveSegStore } from "./store";

type Phase = "loading-volume" | "intro" | "downloading" | "encoding" | "ready" | "decoding" | "saved" | "error";

export function InteractiveSegPanel() {
  const open = useInteractiveSegStore((s) => s.open);
  const toolId = useInteractiveSegStore((s) => s.toolId);
  const nodeId = useInteractiveSegStore((s) => s.nodeId);
  const close = useInteractiveSegStore((s) => s.close);
  const manifest = useCurrentCase();

  const [phase, setPhase] = React.useState<Phase>("loading-volume");
  const [progress, setProgress] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [vol, setVol] = React.useState<NiftiVolume | null>(null);
  const [norm, setNorm] = React.useState<Float32Array | null>(null);
  const [orientation, setOrientation] = React.useState<SliceOrientation>("axial");
  const [index, setIndex] = React.useState(0);
  const [points, setPoints] = React.useState<SamPoint[]>([]);
  const [maskDisplay, setMaskDisplay] = React.useState<Uint8Array | null>(null);
  const [savedPath, setSavedPath] = React.useState<string | null>(null);
  const sessionRef = React.useRef<SamSession | null>(null);

  const reset = React.useCallback(() => {
    sessionRef.current?.dispose();
    sessionRef.current = null;
    setPhase("loading-volume");
    setProgress("");
    setError(null);
    setVol(null);
    setNorm(null);
    setOrientation("axial");
    setIndex(0);
    setPoints([]);
    setMaskDisplay(null);
    setSavedPath(null);
  }, []);

  // Load (just decode) the chosen volume as soon as the panel opens — this is
  // the reader's own already-selected file, not a network fetch of model
  // weights, so it does not wait for "Download & run".
  React.useEffect(() => {
    if (!open) return;
    let live = true;
    reset();
    (async () => {
      const node = manifest ? [...manifest.images, ...manifest.masks].find((n) => n.id === nodeId) : null;
      const abs = node ? nodeAbsPath(manifest, node) : null;
      if (!abs) {
        if (live) {
          setError("Could not resolve that volume's file.");
          setPhase("error");
        }
        return;
      }
      try {
        const { url } = await getAssetUrl(abs);
        const v = await loadVolume(url);
        if (!live) return;
        setVol(v);
        setNorm(percentileClip(v));
        setIndex(Math.floor(sliceDepth(v, "axial") / 2));
        setPhase("intro");
      } catch (err) {
        if (live) {
          setError(err instanceof Error ? err.message : String(err));
          setPhase("error");
        }
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nodeId]);

  React.useEffect(() => {
    if (!open) sessionRef.current?.dispose();
  }, [open]);

  const encodeCurrentSlice = React.useCallback(
    async (v: NiftiVolume, n: Float32Array, o: SliceOrientation, i: number) => {
      if (!sessionRef.current) return;
      setPhase("encoding");
      setPoints([]);
      setMaskDisplay(null);
      try {
        const slice = extractSlice(v, n, o, i);
        const tensor = buildEncoderInput(slice);
        await sessionRef.current.encode(tensor);
        setPhase("ready");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("error");
      }
    },
    [],
  );

  const downloadAndRun = async () => {
    if (!vol || !norm) return;
    setPhase("downloading");
    setError(null);
    try {
      const session = createSamSession();
      sessionRef.current = session;
      await session.load((stage) => setProgress(stage));
      await encodeCurrentSlice(vol, norm, orientation, index);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("error");
    }
  };

  // Re-encoding already runs on every orientation/slice change (SAM's
  // embedding is per-slice); guarding against a change while one is already
  // in flight keeps two overlapping `encode` calls from racing to set phase.
  const canReencode = phase === "ready" || phase === "decoding";

  const changeOrientation = (o: SliceOrientation) => {
    setOrientation(o);
    if (!vol) return;
    const i = Math.floor(sliceDepth(vol, o) / 2);
    setIndex(i);
    if (canReencode) void encodeCurrentSlice(vol, norm!, o, i);
  };

  const changeIndex = (i: number) => {
    setIndex(i);
    if (vol && norm && canReencode) void encodeCurrentSlice(vol, norm, orientation, i);
  };

  const addPoint = async (p: SamPoint) => {
    if (!vol || !sessionRef.current) return;
    const next = [...points, p];
    setPoints(next);
    setPhase("decoding");
    try {
      const slice = extractSlice(vol, norm!, orientation, index);
      const coords: number[] = [];
      for (const pt of next) {
        const [mx, my] = toModelSpace(pt.x, pt.y, slice.width, slice.height);
        coords.push(mx, my);
      }
      const labels = next.map((pt) => pt.label);
      const result = await sessionRef.current.decode(coords, labels);
      setMaskDisplay(downsampleMask(result.mask, slice.width, slice.height));
      setPhase("ready");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("error");
    }
  };

  const clearPoints = () => {
    setPoints([]);
    setMaskDisplay(null);
  };

  const save = async () => {
    if (!vol || !maskDisplay) return;
    const slice = extractSlice(vol, norm!, orientation, index);
    const voxels = embedMaskIntoVolume(vol.dims, orientation, index, slice.width, slice.height, maskDisplay);
    const bytes = await niftiGz({ dims: vol.dims, pixdim: vol.pixdim, affine: vol.affine, voxels, datatype: 16 });
    const runId = Date.now().toString(36);
    const relPath = `tools/${toolId === "ml.sam" ? "sam" : "unknown"}/${runId}/mask.nii.gz`;
    registerToolOutput(relPath, new Blob([bytes as BlobPart], { type: "application/gzip" }), "nifti");
    setSavedPath(relPath);
    setPhase("saved");
  };

  if (!open) return null;

  const busy = phase === "downloading" || phase === "encoding" || phase === "decoding" || phase === "loading-volume";

  return (
    <DialogOverlay align="top" className="z-[76]" onMouseDown={close}>
      <DialogPanel className="max-h-[88vh] w-[720px]" onMouseDown={(e) => e.stopPropagation()}>
        <DialogTitle title="Segment Anything (SAM)" subtitle="Click a point on the slice to segment it." onClose={close} />

        {phase === "error" ? (
          <div className="px-6 pb-6">
            <p className="text-[12px] text-red-700">{error}</p>
            <Button className={cn("mt-3", dialogSecondaryButtonClass)} size="sm" onClick={close}>
              Close
            </Button>
          </div>
        ) : phase === "loading-volume" ? (
          <div className="flex items-center justify-center p-10 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : phase === "intro" ? (
          <div className="space-y-4 px-6 pb-6">
            <p className="text-[12px] leading-relaxed text-ads-text">{SAM_LICENSE_NOTE}</p>
            <p className="text-[11.5px] text-ads-muted">
              About {SAM_TOTAL_MB} MB, fetched once and cached in this browser for next time. Nothing downloads until
              you click below.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={close}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" className={dialogPrimaryButtonClass} onClick={() => void downloadAndRun()}>
                <Download className="h-3.5 w-3.5" />
                Download &amp; run
              </Button>
            </div>
          </div>
        ) : phase === "saved" ? (
          <div className="space-y-3 px-6 pb-6">
            <p className="text-[12.5px] text-ads-text">Saved to Output:</p>
            <p className="font-mono text-[11.5px] text-ads-muted">{savedPath}</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={() => setPhase("ready")}>
                Keep segmenting
              </Button>
              <Button size="sm" variant="primary" className={dialogPrimaryButtonClass} onClick={close}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1" style={{ height: 420 }}>
              <PromptCanvas
                vol={vol}
                norm={norm}
                orientation={orientation}
                index={index}
                onOrientationChange={changeOrientation}
                onIndexChange={changeIndex}
                points={points}
                onAddPoint={(p) => void addPoint(p)}
                maskDisplay={maskDisplay}
                busy={busy}
              />
            </div>
            <DialogFooter>
              <span className={cn("mr-auto text-[11px]", busy ? "text-ads-muted" : "text-transparent")}>
                {progress || "working…"}
              </span>
              <Button size="sm" className={dialogSecondaryButtonClass} disabled={points.length === 0} onClick={clearPoints}>
                Clear points
              </Button>
              <Button size="sm" className={dialogSecondaryButtonClass} onClick={close}>
                Close
              </Button>
              <Button size="sm" variant="primary" className={dialogPrimaryButtonClass} disabled={!maskDisplay || busy} onClick={() => void save()}>
                <Save className="h-3.5 w-3.5" />
                Save to Output
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogPanel>
    </DialogOverlay>
  );
}
